import React, { useEffect, useState, useRef, FC } from "react"
import { UserSelectListItem } from "../components/UserListItem";
import { SelectBox } from "../components/SelectBox"
import { UserType } from "../types/models/User";
import client from "../apis/client";

type TextChangeConditionType = {
    textIsAdded: boolean;
    differentLetter: string | undefined;
    differentLetterIndex: number | undefined;
}

type Props = {
    text: string,
    setText: ( text: string ) => void,
    focusInput: ( number: number ) => void,
    changeLayout: () => void
}

export const MentionModal: FC<Props> = ( {
    text,
    setText,
    focusInput,
    changeLayout
} ) => {

    const [ modalVisible, setModalVisible ] = useState( false )
    const [ teamMemberList, setTeamMemberList ] = useState<UserType[]>( [] )
    const [ belongToTeam, setBelongToTeam ] = useState( true )
    const [ previousText, setPreviousText ] = useState( text )
    const [ scrolling, setScrolling ] = useState( false )
    const [ searchLetters, setSearchLetters ] = useState( "" )
    const [ focusIndex, setFocusIndex ] = useState( 0 )
    const [ listItemHeight, setListItemHeight ] = useState( 40 )
    const [ textChangeCondition, setTextChangeCondition ] = useState<TextChangeConditionType | null>( null )

    const textRef = useRef<string>( text )
    const previousTextRef = useRef<string>( previousText )
    const searchLettersRef = useRef( searchLetters )
    const modalVisibleRef = useRef( modalVisible )
    const scrollContentRef = useRef<HTMLDivElement>( null )
    const teamMemberListRef = useRef( teamMemberList )
    const focusIndexRef = useRef( focusIndex )
    const listItemHeightRef = useRef( listItemHeight )
    const textChangeConditionRef = useRef( textChangeCondition )

    useEffect( () => {
        textRef.current = text
    }, [ text ] )

    useEffect( () => {
        previousTextRef.current = previousText
    }, [ previousText ] )

    useEffect( () => {
        searchLettersRef.current = searchLetters
    }, [ searchLetters ] )

    useEffect( () => {
        modalVisibleRef.current = modalVisible
    }, [ modalVisible ] )

    useEffect( () => {
        teamMemberListRef.current = teamMemberList
    }, [ teamMemberList ] )

    useEffect( () => {
        focusIndexRef.current = focusIndex
    }, [ focusIndex ] )

    useEffect( () => {
        listItemHeightRef.current = listItemHeight
    }, [ listItemHeight ] )

    useEffect( () => {
        textChangeConditionRef.current = textChangeCondition
    }, [ textChangeCondition ] )

    useEffect( () => {
        const matchedMemberList = teamMemberList
            .filter( member => member.name.includes( searchLetters ) )
        matchedMemberList.length - 1 < focusIndex && setFocusIndex( 0 )
    }, [ searchLetters ] )

    useEffect( () => {
        fetchTeamMember()
        scrollContentRef.current && scrollContentRef.current.addEventListener( "scroll", onScroll, { passive: true } )
        window.addEventListener( "keydown", onKeydown )
        return () => {
            scrollContentRef.current && scrollContentRef.current.removeEventListener( "scroll", onScroll )
            window.removeEventListener( "keydown", onKeydown )
        }
    }, [] )

    useEffect( () => {
        changeLayout()
    }, [ modalVisible ] )

    useEffect( () => {
        scrolling ?
            preventScroll() :
            allowScroll()
    }, [ scrolling ] )

    const onScroll = () => {
        let timeoutId: NodeJS.Timeout | undefined = undefined
        !scrolling && setScrolling( true )
        timeoutId && clearTimeout( timeoutId );
        timeoutId = setTimeout( function () {
            setScrolling( false )
        }, 500 );
    }

    const preventScroll = () => {
        const htmlElement = document.getElementsByTagName( "html" )[ 0 ]
        htmlElement.style.overflow = "hidden"
    }

    const allowScroll = () => {
        const htmlElement = document.getElementsByTagName( "html" )[ 0 ]
        htmlElement.style.overflowY = "auto"
    }

    const onKeydown = ( e: KeyboardEvent ) => {

        if ( !modalVisibleRef.current ) return

        const matchedMemberList = teamMemberListRef.current
            .filter( member => member.name.includes( searchLettersRef.current ) )
        const matchedMemberLastIndex = matchedMemberList.length - 1
        const currentFocusIndex = focusIndexRef.current
        let newFocusIndex
        if ( e.which === 38 ) {
            newFocusIndex = 0 < currentFocusIndex ?
                currentFocusIndex - 1 :
                matchedMemberLastIndex
            setFocusIndex( newFocusIndex )
            e.preventDefault()
            scrollContentRef.current && scrollContentRef.current.scrollTo( {
                top: listItemHeightRef.current * ( newFocusIndex - 1 ),
                behavior: 'smooth'
            } )
        }
        if ( e.which === 40 ) {
            newFocusIndex = currentFocusIndex < matchedMemberLastIndex ?
                currentFocusIndex + 1 :
                0
            setFocusIndex( newFocusIndex )
            e.preventDefault()
            scrollContentRef.current && scrollContentRef.current.scrollTo( {
                top: listItemHeightRef.current * ( newFocusIndex - 1 ),
                behavior: 'smooth'
            } )
        }
        if ( e.which === 13 ) {
            const user = matchedMemberList[ focusIndexRef.current ]
            addUserNameToText( user )
            e.preventDefault()
        }
        // escape
        if ( e.which === 27 ) {
            setModalVisible( false )
            e.preventDefault()
        }
    }

    useEffect( () => {
        if ( !belongToTeam ) return
        const textChangeCondition = detectChangedLetter()

        setTextChangeCondition( textChangeCondition )
        setSearchLetters( getSearchLetters( textChangeCondition.differentLetterIndex ) )

        // @が追加されたらmodal出す
        if ( textChangeCondition.textIsAdded && textChangeCondition.differentLetter === "@" ) {
            setModalVisible( true )
        }
        // @が消えたらmodal消す
        if ( !textChangeCondition.textIsAdded && textChangeCondition.differentLetter === "@" ) {
            setModalVisible( false )
        }
        setPreviousText( text )
    }, [ text ] )


    // 引数追加
    const getSearchLetters = ( differentLetterIndex: number | undefined ) => {
        if ( !textRef.current.includes( "@" ) ) return ""
        if ( !textRef.current ) return ""
        if ( typeof differentLetterIndex === "undefined" ) return ""
        // const { differentLetterIndex } = detectChangedLetter() 消した
        const lastAtmarkIndexBeforeDifferentLetter = getLastAtmarkIndexBeforeDifferentLetter( differentLetterIndex )
        if ( typeof lastAtmarkIndexBeforeDifferentLetter === "undefined" ) return ""
        return textRef.current.slice( lastAtmarkIndexBeforeDifferentLetter + 1, differentLetterIndex + 1 )
    }


    const getLastAtmarkIndexBeforeDifferentLetter = ( differentLetterIndex: number ) => {
        let lastAtmarkIndexBeforeDifferentLetter
        for ( let index = 0; index < textRef.current.length; index++ ) {
            if ( differentLetterIndex < index ) break
            if ( textRef.current[ index ] === "@" ) lastAtmarkIndexBeforeDifferentLetter = index
        }
        return lastAtmarkIndexBeforeDifferentLetter
    }

    // 追加された、もしくは削除された文字を検出
    const detectChangedLetter = () => {
        const textIsAdded = previousTextRef.current.length < textRef.current.length
        const moreLetterText = textIsAdded ?
            textRef.current :
            previousTextRef.current

        const lessLetterText = textIsAdded ?
            previousTextRef.current :
            textRef.current
        let differentLetterIndex: number | undefined, differentLetter: string | undefined
        for ( let index = 0; index < moreLetterText.length; index++ ) {
            if ( moreLetterText[ index ] !== lessLetterText[ index ] ) {
                differentLetterIndex = index
                differentLetter = moreLetterText[ index ]
                break
            }
        }

        return {
            textIsAdded,
            differentLetter,
            differentLetterIndex
        }

    }


    const fetchTeamMember = async () => {
        await client.get( `/api/community/me/`, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        } )
            .then( response => {
                setBelongToTeam( true )
                setTeamMemberList( response.data.member )
            } )
            .catch( error => {
                console.log( error )
                error.response.status === 400 && setBelongToTeam( false )
            } )
    }

    const addUserNameToText = ( user: UserType ) => {
        if ( !textChangeConditionRef.current ) return
        const { differentLetterIndex } = textChangeConditionRef.current
        if ( typeof differentLetterIndex === "undefined" ) return
        const lastAtmarkIndexBeforeDifferentLetter = getLastAtmarkIndexBeforeDifferentLetter( differentLetterIndex )
        if ( typeof lastAtmarkIndexBeforeDifferentLetter === "undefined" ) return
        const formerHalf = textRef.current.slice( 0, lastAtmarkIndexBeforeDifferentLetter + 1 )
        const latterHalf = textRef.current.slice( lastAtmarkIndexBeforeDifferentLetter + 1 + searchLettersRef.current.length )
        const newText = formerHalf + user.name + latterHalf
        setText( newText )
        const savedIndex = focusIndexRef.current
        saveIndex( savedIndex )
        setModalVisible( false )
        const cursorPosition = ( formerHalf + user.name ).length
        setTimeout( () => focusInput( cursorPosition ), 500 )
        // setSearchLetters( "" )
    }

    const saveIndex = ( savedIndex: number ) =>
        setTimeout( () => setFocusIndex( savedIndex )
            , 500 )

    const matchedMemberList = teamMemberList
        .filter( member => member.name.includes( searchLetters ) )

    return (
        <SelectBox
            visible={ !!matchedMemberList.length && modalVisible }
            closeModal={ () => setModalVisible( false ) }
            scrollContentRef={ scrollContentRef }>
            <ul>
                { matchedMemberList.map( ( member, index ) =>
                    <UserSelectListItem
                        key={ member.name }
                        user={ member }
                        onClick={ addUserNameToText }
                        isFocused={ index === focusIndex }
                        setListItemHeight={
                            [ 0, matchedMemberList.length - 1 ].includes( index ) ?
                                setListItemHeight :
                                () => { } } />
                ) }
            </ul>
        </SelectBox>
    )
}



